import { useBoolean } from "@/hooks/use-boolean";
import useFormatPrice from "@/hooks/use-format-price";
import HoverInfoIcon from "@babackapp/ui/components/hover-info-icon";
import { Input } from "@babackapp/ui/components/input";
import { cn } from "@babackapp/ui/lib/utils";
import { EditIcon } from "lucide-react";
import { useCallback, useRef } from "react";

interface BillingDetailLineProps {
  label: string;
  price: number | null;
  currency: string;
  editable?: boolean;
  isStroke?: boolean;
  onChange?: (value: number) => void;
}

export default function BillingDetailLine(props: BillingDetailLineProps) {
  if (props.price === null || props.price === undefined) {
    return null;
  }

  const isEditing = useBoolean(false);
  const formatPrice = useFormatPrice();
  const inputRef = useRef<HTMLInputElement>(null);

  const content = formatPrice({
    amount: props.price,
    currency: props.currency,
  });

  const handleChangePrice = useCallback(
    (value: number) => {
      if (props.onChange) {
        props.onChange(isNaN(value) ? 0 : value);
      }
    },
    [props.onChange],
  );

  const onFocus = useCallback(() => {
    isEditing.setTrue();
    setTimeout(() => inputRef.current?.select(), 10);
  }, []);

  const onBlur = useCallback(() => {
    isEditing.setFalse();
    inputRef.current?.blur();
  }, []);

  const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onBlur();
    }
  }, []);

  const priceLength = props.price.toString().length + 1;
  const contentLength = content.length + 1;

  return (
    <div
      className={cn(
        "flex text-sm justify-between text-muted-foreground",
        props.editable && "text-base font-semibold pt-4 text-foreground",
      )}
    >
      <span
        className={cn(
          "font-medium flex-1 inline-flex items-center",
          props.editable && "font-semibold",
        )}
      >
        {props.label}{" "}
        {/*<HoverInfoIcon*/}
        {/*  title="Description"*/}
        {/*  description="Description et explication de la ligne"*/}
        {/*/>*/}
      </span>
      {props.editable ? (
        <div className="relative cursor-pointer">
          <Input
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            type={isEditing.value ? "number" : "text"}
            ref={inputRef}
            className="inline h-auto p-0 bg-transparent text-left font-bold border-none"
            style={{
              width: `calc(${isEditing.value ? priceLength : contentLength}ch + 8px)`,
            }}
            placeholder="refund amount"
            value={isEditing.value ? props.price : content}
            onChange={(e) => handleChangePrice(e.target.valueAsNumber)}
          />
          <div className="absolute pointer-events-none right-0 top-0 bottom-0 flex items-center justify-center">
            <EditIcon className="size-4" />
          </div>
        </div>
      ) : (
        <span className={cn(
          "font-bold",
          props.isStroke && "line-through"
        )}>{content}</span>
      )}
    </div>
  );
}
